import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle, XCircle, Receipt, Image as ImgIcon, Clock, Hourglass } from "@phosphor-icons/react";
import { toast } from "sonner";

function relTime(iso) {
  if (!iso) return "-";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function ProofCard({ proof, onConfirm, onReject }) {
  const [confirming, setConfirming] = useState(false);
  const draft = proof.pending_order || {};
  const items = draft.items || [];
  const total = draft.total || items.reduce((s, i) => s + (i.qty || 1) * (i.price || 0), 0);
  const imgUrl = `${BACKEND}/api/payment-proofs/${proof.id}/image`;
  const token = localStorage.getItem("ventai_token");
  // Add token via a hidden fetch and blob URL (endpoint requires auth)
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!proof.has_image) return;
    let alive = true; let url = null;
    fetch(imgUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => { if (!alive) return; url = URL.createObjectURL(b); setBlobUrl(url); })
      .catch(() => { /* ignore */ });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [proof.id, proof.has_image, imgUrl, token]);

  const confirm = async () => {
    setConfirming(true);
    try {
      await api.post(`/payment-proofs/${proof.id}/confirm`);
      toast.success("Pago verificado. Pedido creado y cliente notificado.");
      onConfirm(proof.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al confirmar"); }
    finally { setConfirming(false); }
  };

  return (
    <div className="border border-[#27272A] bg-[#121214] rounded-lg overflow-hidden" data-testid={`proof-${proof.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr]">
        <div className="bg-[#0A0A0C] flex items-center justify-center min-h-[220px] border-r border-[#27272A]">
          {proof.has_image ? (
            blobUrl ? (
              <a href={blobUrl} target="_blank" rel="noreferrer">
                <img src={blobUrl} alt="Comprobante" className="max-h-[240px] max-w-full object-contain" data-testid={`proof-img-${proof.id}`} />
              </a>
            ) : (
              <div className="text-[#71717A] text-xs">Cargando...</div>
            )
          ) : (
            <div className="flex flex-col items-center gap-2 text-[#71717A] text-xs">
              <Hourglass size={28} />
              <span>Esperando foto</span>
            </div>
          )}
        </div>
        <div className="p-5 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-display text-lg font-medium tracking-tight">{proof.contact_name || "-"}</div>
              <div className="text-xs text-[#71717A] font-mono">{proof.contact_phone}</div>
            </div>
            <span className="text-[10px] text-[#71717A] font-mono-jb flex items-center gap-1">
              <Clock size={10} /> {relTime(proof.created_at)}
            </span>
          </div>
          <div className="mt-3 space-y-1">
            {items.map((it, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span className="text-[#A1A1AA]">{it.qty}× {it.name}</span>
                <span className="text-white font-mono-jb">${(it.qty || 1) * (it.price || 0)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 mt-2 border-t border-[#18181B] text-sm">
              <span className="text-[#A1A1AA]">Total</span>
              <span className="font-mono-jb text-white font-medium">${total}</span>
            </div>
            {draft.address && (
              <div className="text-[11px] text-[#71717A] pt-2">Entrega: <span className="text-white">{draft.address}</span></div>
            )}
          </div>
          <div className="mt-auto pt-4 flex items-center gap-2">
            <Button
              onClick={confirm}
              disabled={confirming || !proof.has_image}
              className="flex-1 bg-[#10B981] hover:bg-[#0EA271] disabled:opacity-40"
              data-testid={`confirm-${proof.id}`}
            >
              <CheckCircle size={14} className="mr-1.5" weight="fill" /> {confirming ? "..." : "Confirmar pago"}
            </Button>
            <Button
              onClick={() => onReject(proof)}
              disabled={!proof.has_image}
              variant="outline"
              className="border-[rgba(239,68,68,0.4)] text-[#EF4444] hover:bg-[rgba(239,68,68,0.1)] hover:text-[#EF4444] disabled:opacity-40"
              data-testid={`reject-${proof.id}`}
            >
              <XCircle size={14} className="mr-1.5" /> Rechazar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Payments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectProof, setRejectProof] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payment-proofs", { params: { status: "pending" } });
      setRows(data);
    } catch { toast.error("Error al cargar comprobantes"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doReject = async () => {
    if (!rejectProof) return;
    try {
      await api.post(`/payment-proofs/${rejectProof.id}/reject`, { reason: rejectReason || "El comprobante no es válido" });
      toast.success("Cliente avisado. Esperando nuevo comprobante.");
      setRejectProof(null);
      setRejectReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const pending = rows.filter((r) => r.has_image);
  const waiting = rows.filter((r) => !r.has_image);

  return (
    <div className="p-8 max-w-6xl mx-auto fade-in" data-testid="payments-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2 flex items-center gap-2">
          <Receipt size={12} weight="fill" /> Verificación
        </div>
        <h1 className="font-display text-4xl font-medium tracking-tighter">Comprobantes de pago</h1>
        <p className="text-sm text-[#A1A1AA] mt-1">Revisa los comprobantes que envían los clientes por WhatsApp. Al confirmar se crea el pedido automáticamente.</p>
      </div>

      {loading && <div className="text-sm text-[#71717A]">Cargando...</div>}

      {!loading && rows.length === 0 && (
        <div className="border border-dashed border-[#27272A] rounded-lg p-12 text-center" data-testid="payments-empty">
          <ImgIcon size={32} className="mx-auto mb-3 text-[#3F3F46]" />
          <div className="text-sm text-[#A1A1AA]">Sin comprobantes pendientes.</div>
          <div className="text-xs text-[#71717A] mt-1">Activa &ldquo;Pago con comprobante&rdquo; en Agente IA para empezar a recibirlos.</div>
        </div>
      )}

      {pending.length > 0 && (
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-3">Para revisar · {pending.length}</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {pending.map((p) => (
              <ProofCard key={p.id} proof={p} onConfirm={load} onReject={setRejectProof} />
            ))}
          </div>
        </div>
      )}

      {waiting.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-3">Esperando foto del cliente · {waiting.length}</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {waiting.map((p) => (
              <ProofCard key={p.id} proof={p} onConfirm={load} onReject={setRejectProof} />
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!rejectProof} onOpenChange={(v) => { if (!v) { setRejectProof(null); setRejectReason(""); } }}>
        <DialogContent className="bg-[#121214] border-[#27272A] text-white" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-[#EF4444]">Rechazar comprobante</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#A1A1AA]">La IA le pedirá al cliente que envíe otro comprobante. Podés indicar el motivo.</p>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="No se ve el monto, foto borrosa, etc."
            className="bg-[#0A0A0C] border-[#27272A] mt-2"
            data-testid="reject-reason"
          />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setRejectProof(null)} className="flex-1 border-[#27272A] bg-[#18181B]">Cancelar</Button>
            <Button onClick={doReject} className="flex-1 bg-[#EF4444] hover:bg-[#DC2626]" data-testid="reject-confirm">
              <XCircle size={14} className="mr-1.5" /> Rechazar y avisar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
